<template>
    <lti-drawer
        class="scorm-student-drawer"
        :active="active"
        :title="drawerTitle"
        :maximized="isDrawerMaximized"
        @close="$emit('close')"
        @update:active="handleUpdateActive"
        @restore="handleRestore"
        @state="handleMaximizedChange"
    >
        <div class="scorm-drawer">
            <!-- Sub-header: SCORM badges + Reset + runtime toggle -->
            <div class="scorm-subheader">
                <div class="scorm-subheader__meta">
                    <span class="scorm-badge scorm-badge--standard tw:text-base">{{ pkg?.standard || 'SCORM 1.2' }}</span>
                    <span class="scorm-badge scorm-badge--version tw:text-base">{{ pkg?.version }}</span>
                </div>

                <div class="scorm-subheader__controls">
                    <!-- SCO selector (when multiple SCOs exist) -->
                    <div v-if="availableScos.length > 1" class="scorm-sco-selector">
                        <label class="scorm-sco-selector__label tw:text-base">SCO:</label>
                        <!-- eslint-disable-next-line vue-a11y/no-onchange -->
                        <select v-model="selectedScoId" class="scorm-sco-selector__select tw-text-sm" @change="onScoChange">
                            <option v-for="sco in availableScos" :key="sco.id" :value="sco.id">
                                {{ sco.title || sco.id }}
                                <span v-if="sco.mastery_score != null"> - {{ Math.round(sco.mastery_score) }}%</span>
                            </option>
                        </select>
                    </div>

                    <button
                        class="scorm-ctrl-btn tw:text-sm"
                        title="Reset attempt"
                        :disabled="resettingAttempt"
                        @click="resetAttempt"
                    >
                        <i class="pi" :class="resettingAttempt ? 'pi-spin pi-spinner' : 'pi-replay'" />
                        Reset
                    </button>
                </div>
            </div>

            <!-- Split body -->
            <div class="scorm-body">
                <!-- SCO iframe -->
                <div class="scorm-player">
                    <div v-if="previewLoading" class="scorm-player__loading">
                        <i class="pi pi-spin pi-spinner tw:text-2xl tw:text-gray-400" />
                        <p class="tw:mt-2 tw:text-sm tw:text-gray-500">Loading preview…</p>
                    </div>
                    <div v-else-if="previewError" class="scorm-player__loading scorm-player__error">
                        <i class="pi pi-exclamation-triangle tw:text-3xl tw:text-amber-500" />
                        <p class="tw:mt-3 tw:text-sm tw:text-gray-600">{{ previewError }}</p>
                        <button class="scorm-retry-btn tw:mt-4 tw:text-sm" @click="loadPreview">
                            <i class="pi pi-refresh" />
                            Retry
                        </button>
                    </div>
                    <div v-else-if="launchUrl && !iframeReady" class="scorm-player__loading">
                        <i class="pi pi-spin pi-spinner tw:text-2xl tw:text-gray-400" />
                        <p class="tw:mt-2 tw:text-sm tw:text-gray-500">Loading SCO…</p>
                    </div>
                    <iframe
                        v-if="launchUrl"
                        ref="scoFrame"
                        :key="iframeKey"
                        :src="launchUrl"
                        class="scorm-iframe"
                        :class="{ 'scorm-iframe--ready': iframeReady }"
                        title="E-learning package preview"
                        @load="onIframeLoad"
                    />
                    <div v-else class="scorm-player__loading">
                        <i class="pi pi-box tw:text-3xl tw:text-gray-300" />
                        <p class="tw:mt-3 tw:text-sm tw:text-gray-400">No launchable SCO found for this package.</p>
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <e-button type="is-light" outlined class="decline-report-close-btn mr-4" @click="close">
                <i class="pi pi-times tw:mr-1" aria-hidden="true" />
                Close
            </e-button>
        </template>
    </lti-drawer>
</template>

<script>
import LtiDrawer from '@/components/lti/LtiDrawer.vue';
import UserSetting from '@/models/UserSetting.js';
import { fetchPackageLaunch, fetchPackagePreview } from '@/utils/scorm12Api.js';

const LOAD_DEBOUNCE_MS = 50;

export default {
    name: 'ScormStudentDrawer',

    components: { LtiDrawer },

    props: {
        active: { type: Boolean, default: false },
        pkg: { type: Object, default: null },
        // Prefer hiding runtime for student by default
        hideRuntime: { type: Boolean, default: true },
        itemId: { type: [Number, String], default: null },
        activityId: { type: [Number, String], default: null },
        courseId: { type: [Number, String], default: null },
        // If provided, preview as this user id (server may log attempts)
        userId: { type: [Number, String], default: null },
        // When true (course activity only), persist drawer size between sessions
        rememberDrawerState: { type: Boolean, default: false },
    },

    emits: ['close', 'progress-synced'],

    data() {
        return {
            isDrawerMaximized: false,
            iframeReady: false,
            previewLoading: false,
            previewError: null,
            launchUrl: null,
            iframeKey: 0,
            loadToken: 0,
            selectedScoId: null,
            resettingAttempt: false,
            attemptId: null,
            _loadDebounceTimer: null,
            _progressPollTimer: null,
        };
    },

    computed: {
        drawerTitle() {
            return this.pkg?.title ?? 'SCORM Preview';
        },

        packageId() {
            return this.pkg?.id ?? null;
        },

        availableScos() {
            // Packages may include SCOs directly as `pkg.scos`, or nested on
            // `pkg.latest_version.scos` / `pkg.latestVersion.scos`, or the
            // package may expose `latest_version.items` which we can map to
            // a minimal SCO shape. Support all those shapes so the selector
            // appears regardless of which API shape the caller passed.
            const scos = this.pkg?.scos ?? this.pkg?.latest_version?.scos ?? this.pkg?.latestVersion?.scos;

            if (Array.isArray(scos) && scos.length) return scos;

            const items = this.pkg?.latest_version?.items ?? this.pkg?.latestVersion?.items ?? [];
            if (Array.isArray(items) && items.length) {
                return items.map(item => ({
                    id: item.id,
                    title: item.title || item.name || `Item ${item.id}`,
                    href: item.launch_path ?? item.href ?? null,
                    mastery_score: item.metadata?.mastery_score ?? item.mastery_score ?? null,
                }));
            }

            return [];
        },
    },

    watch: {
        // hideRuntime handled by prop; no runtime UI in student drawer

        active(val) {
            if (val) {
                this.syncDrawerStateOnOpen();
                this.initializeSelectedSco();
                this.debounceLoadPreview();
                this.startProgressPolling();
            } else {
                this.resetPreview();
                this.stopProgressPolling();
            }
        },

        packageId() {
            if (this.active) {
                this.initializeSelectedSco();
                this.debounceLoadPreview();
            }
        },

        itemId() {
            if (this.active && this.itemId) {
                this.selectedScoId = this.itemId;
                this.debounceLoadPreview();
            }
        },

        availableScos() {
            if (this.active && !this.selectedScoId) {
                this.initializeSelectedSco();
            }
        },
    },

    mounted() {
        if (this.active) {
            this.syncDrawerStateOnOpen();
        }
    },

    beforeUnmount() {
        clearTimeout(this._loadDebounceTimer);
        this.stopProgressPolling();
    },

    methods: {
        close() {
            this.$emit('close');
        },

        handleUpdateActive(val) {
            if (!val) {
                // stop polling before notifying parent so no final progress event races
                this.stopProgressPolling();
                this.$emit('close');
            }
        },
        initializeSelectedSco() {
            // Priority: itemId prop > first SCO > null
            if (this.itemId) {
                this.selectedScoId = this.itemId;
            } else if (this.availableScos.length > 0) {
                this.selectedScoId = this.availableScos[0].id;
            } else {
                this.selectedScoId = null;
            }
        },

        onScoChange() {
            if (this.active) {
                this.debounceLoadPreview();
            }
        },

        debounceLoadPreview() {
            clearTimeout(this._loadDebounceTimer);
            this._loadDebounceTimer = setTimeout(() => {
                this.loadPreview();
            }, LOAD_DEBOUNCE_MS);
        },

        isValidUrl(url) {
            try {
                const parsed = new URL(url, window.location.origin);
                return parsed.protocol === 'http:' || parsed.protocol === 'https:';
            } catch {
                return false;
            }
        },

        async loadPreview() {
            if (!this.active || !this.packageId) {
                return;
            }

            const token = ++this.loadToken;

            this.previewError = null;
            this.launchUrl = null;
            this.iframeReady = false;
            this.previewLoading = true;

            try {
                const data = this.activityId
                    ? await fetchPackageLaunch(this.activityId, this.selectedScoId)
                    : await fetchPackagePreview(this.packageId, this.selectedScoId);

                if (token !== this.loadToken) {
                    return;
                }

                this.attemptId = data.attempt_id ?? null;
                this.launchUrl = data.launch_url ?? null;

                if (!this.launchUrl) {
                    this.previewError = 'Preview URL was not returned for this package.';
                    return;
                }

                if (!this.isValidUrl(this.launchUrl)) {
                    this.previewError = 'Invalid preview URL received from server.';
                    this.launchUrl = null;
                    return;
                }

                this.iframeKey += 1;
            } catch (error) {
                if (token !== this.loadToken) {
                    return;
                }

                this.previewError = error?.response?.data?.message || 'This package is not ready to preview yet.';
            } finally {
                if (token === this.loadToken) {
                    this.previewLoading = false;
                }
            }
        },

        resetPreview() {
            this.loadToken += 1;
            this.previewLoading = false;
            this.previewError = null;
            this.launchUrl = null;
            this.iframeReady = false;
            this.attemptId = null;
        },

        onIframeLoad() {
            this.iframeReady = true;
        },

        startProgressPolling() {
            if (!this.activityId) {
                return;
            }

            this.stopProgressPolling();
            this._progressPollTimer = setInterval(() => {
                this.$emit('progress-synced');
            }, 15000);
        },

        stopProgressPolling() {
            clearInterval(this._progressPollTimer);
            this._progressPollTimer = null;
        },

        async resetAttempt() {
            this.resettingAttempt = true;
            try {
                await this.loadPreview();
            } finally {
                this.resettingAttempt = false;
            }
        },

        handleRestore(payload) {
            if (!this.rememberDrawerState || payload?.relaunch === false) {
                return;
            }

            if (this.active) {
                this.debounceLoadPreview();
            }
        },

        syncDrawerStateOnOpen() {
            if (!this.rememberDrawerState) {
                this.isDrawerMaximized = false;
                return;
            }

            this.loadSavedDrawerState();
        },

        loadSavedDrawerState() {
            try {
                const setting = UserSetting.findByKey('elearning_package_drawer_state');
                const savedState = setting?.value;

                if (savedState === 'fullscreen') {
                    this.isDrawerMaximized = true;
                } else {
                    this.isDrawerMaximized = false;
                }
            } catch {
                this.$edverumToast?.open({
                    message: 'Failed to load eLearning package drawer state',
                    type: 'is-warning',
                });
                this.isDrawerMaximized = false;
            }
        },

        async handleMaximizedChange(isMaximized) {
            this.isDrawerMaximized = isMaximized;

            if (!this.rememberDrawerState) {
                return;
            }

            await this.saveDrawerState(isMaximized);
        },

        async saveDrawerState(isMaximized) {
            if (!this.rememberDrawerState) {
                return;
            }

            try {
                const setting = UserSetting.findByKey('elearning_package_drawer_state');
                const state = isMaximized ? 'fullscreen' : 'normal';
                await setting.set(state);
            } catch {
                this.$edverumToast?.open({
                    message: 'Failed to save eLearning package drawer state',
                    type: 'is-warning',
                });
            }
        },
    },
};
</script>

<style scoped>
/* Drawer width — stay partial (75vw) unless the user maximizes via LtiDrawer */
:global(.scorm-student-drawer.lti-drawer:not(.is-maximized)) {
    --lti-sidebar-width: 75%;
    --lti-sidebar-maxwidth: 75vw;
}

:global(.scorm-student-drawer.lti-drawer .card-content) {
    display: flex;
    flex-direction: column;
    min-height: 0;
    min-width: 0;
    overflow-x: clip;
}

/* Partial-width SCORM drawer on mobile: keep header controls inside the panel */
:global(.scorm-student-drawer.lti-drawer .card-header) {
    padding-left: 0.75rem;
    padding-right: 0.25rem;
}

@media screen and (max-width: 480px) {
    :global(.scorm-student-drawer.lti-drawer .card-header .card-header-icon) {
        padding-right: 0.125rem;
    }

    :global(.scorm-student-drawer.lti-drawer .card-header .edverum-logo-compact) {
        display: none;
    }
}

/* ── Drawer root (fill sidebar; player gets full height immediately) ── */
.scorm-drawer {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    min-width: 0;
    height: 100%;
    overflow: hidden;
    background: var(--surface-card, var(--p-content-background, transparent));
    color: var(--text-color, var(--p-text-color, inherit));
}

/* ── Sub-header ── */
.scorm-subheader {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 0 12px;
    border-bottom: 1px solid var(--surface-border, var(--p-content-border-color, #e5e7eb));
    margin-bottom: 0;
    flex-shrink: 0;
    min-width: 0;
    overflow-x: clip;
}

.scorm-subheader__meta,
.scorm-subheader__controls {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.scorm-subheader__meta {
    flex: 0 0 auto;
    flex-wrap: wrap;
}

.scorm-subheader__controls {
    flex: 1 1 auto;
    min-width: 0;
    justify-content: flex-end;
}

.scorm-badge {
    display: inline-block;
    padding: 2px 9px;
    border-radius: 4px;
    font-weight: 600;
}

.scorm-badge--standard {
    background: var(--p-primary-color, #2563eb);
    color: var(--p-primary-contrast-color, #fff);
}

.scorm-badge--version {
    border: 1px solid var(--p-primary-color, #2563eb);
    color: var(--p-primary-color, #2563eb);
    background: transparent;
}

.scorm-ctrl-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: transparent;
    border: 1px solid var(--surface-border, var(--p-content-border-color, #e5e7eb));
    color: var(--text-color, var(--p-text-color, #374151));
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 5px;
    transition: background 0.15s;
}

.scorm-ctrl-btn:hover:not(:disabled) {
    background: var(--surface-hover, var(--p-content-hover-background, #f3f4f6));
}

.scorm-ctrl-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.scorm-ctrl-btn--icon {
    padding: 4px 8px;
}

.scorm-ctrl-btn--active {
    background: color-mix(in srgb, var(--p-primary-color, #2563eb) 12%, transparent);
    border-color: color-mix(in srgb, var(--p-primary-color, #2563eb) 45%, transparent);
    color: var(--p-primary-color, #1d4ed8);
}

/* SCO selector */
.scorm-sco-selector {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1 1 0;
    min-width: 0;
}

.scorm-subheader__controls .scorm-ctrl-btn {
    flex-shrink: 0;
}

.scorm-sco-selector__label {
    font-weight: 600;
    color: var(--text-color-secondary, var(--p-text-muted-color, #6b7280));
    text-transform: uppercase;
}

.scorm-sco-selector__select {
    padding: 4px 8px;
    border: 1px solid var(--surface-border, var(--p-content-border-color, #d1d5db));
    border-radius: 4px;
    color: var(--text-color, var(--p-text-color, #374151));
    background: var(--surface-card, var(--p-content-background, #fff));
    cursor: pointer;
    flex: 1;
    min-width: 0;
    max-width: 240px;
    transition: border-color 0.15s;
}

.scorm-sco-selector__select:hover {
    border-color: var(--p-surface-400, #9ca3af);
}

.scorm-sco-selector__select:focus {
    outline: none;
    border-color: var(--p-primary-color, #2563eb);
    box-shadow: 0 0 0 3px color-mix(in srgb, var(--p-primary-color, #2563eb) 20%, transparent);
}

@media screen and (max-width: 900px) {
    .scorm-subheader__controls {
        flex: 1 1 100%;
        width: 100%;
    }

    .scorm-sco-selector__select {
        max-width: none;
    }
}

/* ── Split body (full height immediately; no layout transitions) ── */
.scorm-body {
    display: flex;
    flex: 1;
    min-height: 0;
    height: 100%;
    overflow: hidden;
}

/* ── Player pane ── */
.scorm-player {
    flex: 1;
    min-width: 0;
    min-height: 0;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--p-surface-50, #f9fafb);
    border-right: 1px solid var(--surface-border, var(--p-content-border-color, #e5e7eb));
    position: relative;
    overflow: hidden;
}

.scorm-player__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 24px;
    text-align: center;
}

.scorm-player__error {
    max-width: 360px;
}

.scorm-retry-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: #2563eb;
    color: #fff;
    border: none;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
}

.scorm-retry-btn:hover {
    background: #1d4ed8;
}

.scorm-iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
    opacity: 0;
    transition: opacity 0.2s;
}

.scorm-iframe--ready {
    opacity: 1;
}

/* ── Runtime panel ── */
.scorm-runtime {
    width: 340px; /* RUNTIME_PANEL_WIDTH_PX constant */
    flex-shrink: 0;
    min-height: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    background: var(--p-surface-100, #f3f4f6);
    overflow: hidden;
    border-left: 1px solid var(--surface-border, var(--p-content-border-color, #e5e7eb));
}

.scorm-runtime__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid var(--surface-border, var(--p-content-border-color, #e5e7eb));
}

.scorm-runtime__title {
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--text-color-secondary, var(--p-text-muted-color, #6b7280));
    text-transform: uppercase;
}

.scorm-runtime__hide {
    background: transparent;
    border: none;
    color: var(--p-primary-color, #3b82f6);
    cursor: pointer;
}

/* When API log is hidden, show a small control to reopen it */
.scorm-runtime__show {
    background: transparent;
    border: none;
    color: var(--text-color, var(--p-text-color, #374151));
    cursor: pointer;
}

.scorm-runtime__hide:hover {
    text-decoration: underline;
}

.scorm-runtime__section {
    padding: 12px 14px;
    border-bottom: 1px solid var(--surface-border, var(--p-content-border-color, #e5e7eb));
    flex-shrink: 0;
}

.scorm-runtime__section--cmi {
    flex-shrink: 0;
}

.scorm-runtime__section--log {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-bottom: none;
}

.scorm-runtime__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.scorm-runtime__section-label {
    font-weight: 600;
    color: var(--text-color, var(--p-text-color, #374151));
    margin-bottom: 0;
}

.scorm-runtime__section--cmi .scorm-runtime__section-label {
    margin-bottom: 8px;
}

/* CMI table */
.cmi-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Courier New', Courier, monospace;
}

.cmi-table__key {
    color: var(--text-color-secondary, var(--p-text-muted-color, #6b7280));
    padding: 2px 0;
    width: 60%;
}

.cmi-table__value {
    color: var(--text-color, var(--p-text-color, #111827));
    font-weight: 500;
    text-align: right;
}

.cmi-table__value--failed {
    color: #dc2626;
}
.cmi-table__value--passed {
    color: #16a34a;
}

/* API log */
.api-log {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    font-family: 'Courier New', Courier, monospace;
    line-height: 1.6;
}

.api-log__entry {
    padding: 3px 0;
    border-bottom: 1px solid var(--surface-border, var(--p-content-border-color, #e5e7eb));
    color: var(--text-color, var(--p-text-color, #374151));
    word-break: break-all;
}

.api-log__entry:last-child {
    border-bottom: none;
}

.decline-report-close-btn {
    min-width: 5.5rem;
    min-height: 44px;
    border: 1.5px solid var(--surface-border, var(--p-content-border-color, #2b80d6));
    color: var(--text-color, var(--p-text-color, #343638));
    border-radius: 8px;
    padding: 0.45rem 0.85rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
}

.decline-report-close-btn i {
    color: inherit;
}

.decline-report-close-btn:hover {
    background: var(--surface-hover, var(--p-content-hover-background, rgba(43, 128, 214, 0.06)));
    border-color: #1f6fb8;
}

</style>

<!--
  Unscoped: LtiDrawer teleports this chrome to <body>. html.app-dark is the
  theme ancestor. Do not wrap it in a leading global() pseudo — Vue scoped
  CSS would discard the rest of the selector and paint <html>.
-->
<style>
html.app-dark .scorm-drawer {
    background: var(--p-surface-900, #111827);
    color: var(--p-text-color, #f9fafb);
}

html.app-dark .scorm-subheader {
    border-bottom-color: var(--p-surface-700, #374151);
}

html.app-dark .scorm-ctrl-btn {
    border-color: var(--p-surface-600, #4b5563);
    color: var(--p-text-color, #f9fafb);
}

html.app-dark .scorm-ctrl-btn:hover:not(:disabled) {
    background: var(--p-surface-800, #1f2937);
}

html.app-dark .scorm-ctrl-btn--active {
    background: color-mix(in srgb, var(--p-primary-color, #3b82f6) 18%, transparent);
    border-color: var(--p-primary-color, #3b82f6);
    color: var(--p-primary-color, #93c5fd);
}

html.app-dark .scorm-badge--version {
    border-color: var(--p-primary-color, #3b82f6);
    color: var(--p-primary-color, #93c5fd);
}

html.app-dark .scorm-sco-selector__label {
    color: var(--p-text-muted-color, #9ca3af);
}

html.app-dark .scorm-sco-selector__select {
    background: var(--p-surface-800, #1f2937);
    border-color: var(--p-surface-600, #4b5563);
    color: var(--p-text-color, #f9fafb);
}

html.app-dark .scorm-player {
    background: var(--p-surface-950, #030712);
    border-right-color: var(--p-surface-700, #374151);
}

html.app-dark .scorm-player__loading p {
    color: var(--p-text-muted-color, #9ca3af);
}

html.app-dark .scorm-runtime {
    background: var(--p-surface-900, #111827);
    border-left-color: var(--p-surface-700, #374151);
}

html.app-dark .scorm-runtime__header,
html.app-dark .scorm-runtime__section,
html.app-dark .api-log__entry {
    border-color: var(--p-surface-700, #374151);
}

html.app-dark .scorm-runtime__title,
html.app-dark .cmi-table__key {
    color: var(--p-text-muted-color, #9ca3af);
}

html.app-dark .scorm-runtime__show,
html.app-dark .scorm-runtime__section-label,
html.app-dark .cmi-table__value,
html.app-dark .api-log__entry {
    color: var(--p-text-color, #f9fafb);
}

html.app-dark .decline-report-close-btn {
    border-color: var(--p-primary-color, #3b82f6);
    color: var(--p-text-color, #f9fafb);
}

/* Ported out of the scoped block. Vue scoped CSS collapses a leading global
 * pseudo-selector to a bare `.app-dark` rule: the descendant part is discarded
 * and the declarations land on <html>. `html.app-dark <sel>` compiles correctly. */
html.app-dark .decline-report-close-btn:hover {
    background: rgba(31,111,184,0.06);
}

html.app-dark .scorm-player__loading i {
    color: var(--p-text-muted-color, #9ca3af);
}

html.app-dark .scorm-player__error i {
    color: var(--p-warning, #d97706);
}

html.app-dark .scorm-retry-btn {
    background: var(--p-primary-color, #2563eb);
    color: var(--p-primary-contrast-color, #fff);
    border: none;
}

html.app-dark .scorm-retry-btn:hover {
    background: var(--p-primary-600, #1d4ed8);
}
</style>

